// @ts-nocheck
import AddToCartButtonWithIcon, {
    AddToCartButtonWithIconStyles,
} from "@store/Library/Components/Common/AddToCartButtonWithIcon";
import useCanSeePrices from "@store/Framework/Store/Data/Accounts/Hooks/useCanSeePrices";
import useProductContext from "@store/Framework/Store/Data/Products/ProductsHooks/useProductContext";
import { TextFieldPresentationProps } from "@store/MobiusOverrides/TextField/TextField";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { WishListLineModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconMemo, IconProps } from "@insite/mobius/Icon";
import { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Link, { LinkProps } from "@store/MobiusOverrides/Link/Link";
import { GridContainerProps } from "@store/MobiusOverrides/GridContainer/GridContainer";
import { ProductAvailabilityStyles } from "@store/Overrides/Components/ProductAvailability";
import ProductQuantityOrdered from "@store/Overrides/Components/ProductQuantityOrdered";
import { linkStyles } from "@store/Themes/common/mixins/link";
import { productLineProps } from "@store/Themes/common/props/productLineProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import CheckmarkCircle2Fill from "@toolkit/Library/Components/Icons/CheckmarkCircle2/CheckmarkCircle2Fill";
import Refresh from "@toolkit/Library/Components/Icons/Refresh";
import React from "react";
import { css } from "styled-components";
import Clickable from "@insite/mobius/Clickable";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";

interface MyListDetailsProductListLineStyles {
    productControlsGridItem?: GridItemProps;
    productControls?: GridContainerProps;
    productControlsItem?: GridItemProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityOrderedStyles?: TextFieldPresentationProps;
    updateQuantityWrapper?: GridContainerProps;
    updateQuantityLink?: LinkProps;
    quantityUpdatedIcon?: IconProps;
    quantityUpdatedText?: TypographyProps;
    extendedUnitNetPriceText?: TypographyProps;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    notesHidden?: HiddenProps;
    notesGridItem?: GridItemProps;
    notesStyles?: SmallHeadingAndTextStyles;
    addToCartButton?: AddToCartButtonWithIconStyles;
    deleteButtonWrapper?: InjectableCss;
    removeProductButton?: LinkProps;
    removeListLineIcon?: IconProps;
    // bunzl custom Code
    addToCartAndAddToListRow?: InjectableCss;
}

interface MyListDetailsProductListLineQuantityProps {
    wishListLine: WishListLineModel;
    extendedPrice: number;
    canEditWishList: boolean;
    quantityUpdateOnSuccess?: () => void;
    extendedStyles?: MyListDetailsProductListLineStyles;
    onUpdateQuantityClick: () => void;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
}

const myListDetailsProductListLineStyles: MyListDetailsProductListLineStyles = {
    removeProductButton: {
        css: css`
            ${productLineProps.removeProductButton?.css}
            margin-left: ${pxToRem(10)};
        `,
    },
    removeListLineIcon: productLineProps.removeProductLineIcon,

    deleteButtonWrapper: {
        css: css`
            ${breakpoint("sm", "down")} {
                position: absolute;
                right: 0;
                top: 0.5rem;
            }
            min-width: ${pxToRem(36)};
            margin-block: auto;
            margin-left: ${pxToRem(16)};
        `,
    },
    productControlsGridItem: { ...productLineProps.productControlsGridItem },
    addToCartButton: {
        button: {
            typographyProps: {
                css: css`
                    @media (min-width: 1200px) and (max-width: 1500px) {
                        font-size: ${pxToRem(14)};
                    }
                `,
            },
            css: css`
                ${({ theme }) => theme.button?.primary?.css}

                min-width: 10em;
                margin-block: 0.5rem;
                align-self: center;

                & span:first-of-type {
                    @media (min-width: 1200px) and (max-width: 1350px) {
                        margin-right: ${pxToRem(14)};
                    }
                }
            `,
        },
    },
    productControls: productLineProps.productControls,
    productControlsItem: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            margin: 0.5em 0;
            gap: ${pxToRem(8)};
        `,
    },
    productUnitOfMeasureSelectStyles: {
        labelProps: {
            css: css`
                @media print {
                    font-size: ${pxToRem(12)};
                }
            `,
        },
        iconProps: {
            css: css`
                @media print {
                    display: none;
                }
            `,
        },
    },
    productQuantityOrderedStyles: productLineProps.quantityOrdered,
    updateQuantityWrapper: {
        css: css`
            display: flex;
            align-items: center;
        `,
    },
    updateQuantityLink: {
        typographyProps: {
            css: css`
                width: 100%;
                font-size: ${x => x.theme.design.typography.size.smallest};
            `,
        },
        icon: {
            iconProps: {
                src: Refresh,
                size: 16,
                color: "secondary",
            },
        },
        css: css`
            ${linkStyles()}
        `,
    },
    quantityUpdatedIcon: {
        src: CheckmarkCircle2Fill,
        size: 16,
        color: "success.main",
        css: css`
            margin-right: ${pxToRem(5)};
        `,
    },
    quantityUpdatedText: {
        css: css`
            color: ${getColor("success.main")};
            font-size: ${x => x.theme.design.typography.size.smallest};
        `,
    },
    extendedUnitNetPriceText: productLineProps.extendedUnitNetPriceText,
    productAvailabilityStyles: productLineProps.productAvailability,
    notesHidden: {
        above: "sm",
        css: css`
            @media print {
                display: none;
            }
            width: 100%;
        `,
    },
    notesGridItem: { width: 12 },
    // bunzl custom Code
    addToCartAndAddToListRow: productLineProps.addToCartAndAddToListRow,
};
const MyListDetailsProductListLineQuantity: React.FC<MyListDetailsProductListLineQuantityProps> = ({
    wishListLine,
    canEditWishList,
    extendedPrice,
    onUpdateQuantityClick,
    onDeleteClick,
}) => {
    const { product, productInfo } = useProductContext();
    const styles = myListDetailsProductListLineStyles;
    const settingsCollection = useSettingsCollection();
    const canSeePrices = useCanSeePrices();
    const isQuantityUpdated = useAppSelector(
        state => state.pages.myListDetails.wishListLinesWithUpdatedQuantity[wishListLine.id],
    );
    const showInventory = settingsCollection.productSettings.showInventoryAvailability;
    const showUnitOfMeasure = product.unitOfMeasures && product.unitOfMeasures.length > 1;
    const showUpdateQuantityLink =
        canEditWishList && productInfo.qtyOrdered > 0 && wishListLine.qtyOrdered !== productInfo.qtyOrdered;
    const showQuantityIsUpdated = isQuantityUpdated && wishListLine.qtyOrdered === productInfo.qtyOrdered;
    const showExtendedPrice = canSeePrices && !product.quoteRequired && !productInfo.pricing?.requiresRealTimePrice;
    return (
        <GridItem {...styles.productControlsGridItem}>
            <StyledWrapper {...styles.productControls}>
                <StyledWrapper {...styles.productControlsItem}>
                    {showUnitOfMeasure && (
                        <ProductUnitOfMeasureSelect
                            disabled={canEditWishList}
                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                        />
                    )}
                    <ProductQuantityOrdered
                        extendedStyles={styles.productQuantityOrderedStyles}
                        labelOverride={false}
                        canEdit={true}
                    />

                    <StyledWrapper {...styles.updateQuantityWrapper}>
                        {showUpdateQuantityLink && (
                            <Link {...styles.updateQuantityLink} onClick={onUpdateQuantityClick}>
                                {translate("Update QTY")}
                            </Link>
                        )}
                        {showQuantityIsUpdated && (
                            <>
                                <Icon {...styles.quantityUpdatedIcon} />
                                <Typography {...styles.quantityUpdatedText}>{translate("QTY updated")}</Typography>
                            </>
                        )}
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.productControlsItem}>
                    {showExtendedPrice && (
                        <Typography {...styles.extendedUnitNetPriceText}>
                            <LocalizedCurrency amount={extendedPrice} />
                        </Typography>
                    )}
                </StyledWrapper>
                {showInventory && (
                    <StyledWrapper {...styles.productControlsItem}>
                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                    </StyledWrapper>
                )}
                {wishListLine.notes && (
                    <Hidden {...styles.notesHidden}>
                        <GridItem {...styles.notesGridItem}>
                            <SmallHeadingAndText
                                heading={translate("Notes")}
                                text={wishListLine.notes}
                                extendedStyles={styles.notesStyles}
                            />
                        </GridItem>
                    </Hidden>
                )}
                <StyledWrapper {...styles.addToCartAndAddToListRow}>
                    <AddToCartButtonWithIcon
                        notes={wishListLine.notes}
                        extendedStyles={styles.addToCartButton}
                        data-test-selector="addToCart"
                        labelOverride={translate("Add to Cart (List)")}
                    />

                    <>
                        {canEditWishList && (
                            <Clickable
                                {...styles.removeProductButton}
                                onClick={() => onDeleteClick(wishListLine)}
                                data-test-selector="removeRestricted"
                            >
                                <IconMemo {...styles.removeListLineIcon} />
                                <VisuallyHidden>{translate("Removed this product")}</VisuallyHidden>
                            </Clickable>
                        )}
                    </>
                </StyledWrapper>
            </StyledWrapper>
        </GridItem>
    );
};

export default MyListDetailsProductListLineQuantity;
